import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../utils/helpers';

interface SettingCardProps {
  title?: string;
  description?: string;
  icon?: LucideIcon;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** When true, children are not padded (e.g. a flush list of rows). */
  flush?: boolean;
}

/** A titled white card used to group related settings on a page. */
export default function SettingCard({
  title,
  description,
  icon: Icon,
  action,
  children,
  className,
  flush,
}: SettingCardProps) {
  return (
    <section className={cn('bg-white dark:bg-neutral-900 rounded-3xl border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none', className)}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
          <div className="flex items-start gap-3">
            {Icon && (
              <div className="w-10 h-10 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-700 dark:text-neutral-300">
                <Icon className="w-5 h-5" />
              </div>
            )}
            <div>
              {title && <h2 className="font-bold text-neutral-900 dark:text-neutral-50">{title}</h2>}
              {description && <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{description}</p>}
            </div>
          </div>
          {action}
        </header>
      )}
      <div className={cn(flush ? '' : 'px-6 pb-6', title || action ? '' : 'pt-6')}>{children}</div>
    </section>
  );
}
